import { PERMISSION_ENTITY_OPERATIONAL } from '@klear/constants'
import { KlearDate } from '@klear/date'
import { toCurrencyCode } from '@klear/money/currency'
import { Money } from '@klear/money'
import isFinite from 'lodash/isFinite'
import isInteger from 'lodash/isInteger'
import isNumber from 'lodash/isNumber'
import isString from 'lodash/isString'
import toNumber from 'lodash/toNumber'

import type { OperationalCreateWire } from '@klear/operational/operational.wire'

export type OperationalEventSnapshot = Omit<OperationalCreateWire, 'orderId' | 'currency'> & {
  type: string
  id?: number
}

export type OperationalJson = {
  orderId: number
  id?: number
  parentId?: number
  title: string
  supplierId?: string | number
  supplierOrganizationName?: string
  number?: string
  incoterm?: string
  amount: Money
  effectiveDate: KlearDate
  endDate?: KlearDate
  notes?: string
}

export class Operational {
  static readonly __klearClass = 'Operational'
  readonly orderId: number
  id?: number
  parentId?: number
  title: string
  supplierId?: string | number
  supplierOrganizationName?: string
  number?: string
  incoterm?: string
  amount: Money
  effectiveDate: KlearDate
  endDate?: KlearDate
  notes?: string

  constructor(props: OperationalJson) {
    this.orderId = props.orderId
    this.id = props.id
    this.parentId = props.parentId
    this.title = props.title
    this.supplierId = props.supplierId
    this.supplierOrganizationName = props.supplierOrganizationName
    this.number = props.number
    this.incoterm = props.incoterm
    this.amount = props.amount
    this.effectiveDate = props.effectiveDate
    this.endDate = props.endDate
    this.notes = props.notes
  }

  toJSON(): OperationalJson {
    const json: OperationalJson = {
      orderId: this.orderId,
      title: this.title,
      amount: this.amount,
      effectiveDate: this.effectiveDate
    }
    if (this.id != null) json.id = this.id
    if (this.parentId != null) json.parentId = this.parentId
    if (this.supplierId != null) json.supplierId = this.supplierId
    if (this.supplierOrganizationName != null) json.supplierOrganizationName = this.supplierOrganizationName
    if (this.number != null) json.number = this.number
    if (this.incoterm != null) json.incoterm = this.incoterm
    if (this.endDate != null) json.endDate = this.endDate
    if (this.notes != null) json.notes = this.notes
    return json
  }

  get isPersisted(): boolean {
    return isNumber(this.id) && this.id > 0
  }

  get supplierIdAsNumber(): number | undefined {
    return operationalSupplierIdAsNumber(this.supplierId)
  }

  assertValid(): void {
    if (!(this.orderId > 0)) {
      throw new Error('Operational event requires an order id')
    }
    if (!this.title.trim()) {
      throw new Error('Operational event title is required')
    }
  }
}

function parseDate(value: unknown, fallback: KlearDate): KlearDate {
  if (KlearDate.is(value)) return value
  if (isString(value) && value.length > 0) return KlearDate.parse(value) ?? fallback
  return fallback
}

export function operationalSupplierIdAsNumber(supplierId: string | number | undefined): number | undefined {
  if (supplierId == null) return undefined
  const parsed = toNumber(supplierId)
  if (!isFinite(parsed) || !isInteger(parsed) || parsed < 1) return undefined
  return parsed
}

export function operationalFromEventData(
  orderId: number,
  data: Omit<OperationalCreateWire, 'orderId'>,
  ctx?: { id?: number; parentEventId?: number }
): Operational {
  const effectiveDate = parseDate(data.effectiveDate, KlearDate.today())
  const endDate = data.endDate != null ? parseDate(data.endDate, effectiveDate) : undefined

  return new Operational({
    orderId,
    id: ctx?.id,
    parentId: ctx?.parentEventId ?? data.parentEventId,
    title: data.title,
    supplierId: data.supplierId,
    number: data.number ?? undefined,
    incoterm: data.incoterm ?? undefined,
    amount: data.amount != null ? new Money(data.amount) : Money.zero(toCurrencyCode(data.currency)),
    effectiveDate,
    endDate,
    notes: data.notes ?? undefined
  })
}

export function operationalToEventData(operational: Operational): OperationalEventSnapshot {
  const data: OperationalEventSnapshot = {
    type: PERMISSION_ENTITY_OPERATIONAL,
    title: operational.title,
    amount: operational.amount.toJSON(),
    effectiveDate: operational.effectiveDate.toJSON()
  }

  if (operational.id != null && operational.id > 0) data.id = operational.id
  if (operational.supplierId != null) data.supplierId = operational.supplierId
  if (operational.number != null) data.number = operational.number
  if (operational.incoterm != null) data.incoterm = operational.incoterm
  if (operational.endDate != null) data.endDate = operational.endDate.toJSON()
  if (operational.notes != null) data.notes = operational.notes

  return data
}
